import type React from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface SectionCardProps {
	title: string;
	children: ReactNode;
	/** Optional control rendered on the right of the heading (e.g. a details toggle). */
	action?: ReactNode;
	className?: string;
	/** Extra classes for the bordered body panel. */
	bodyClassName?: string;
	/** When false, content renders without the white bordered panel (e.g. quote carousels). */
	bordered?: boolean;
	testId?: string;
}

/**
 * A read-only overview section: a bold heading with an optional right-aligned
 * action, above a white bordered panel that holds the content — same card look
 * as the quote flow sidebar. Headings sit outside the card, no header icons.
 */
const SectionCard = ({
	title,
	children,
	action,
	className,
	bodyClassName,
	bordered = true,
	testId
}: SectionCardProps): React.JSX.Element => {
	return (
		<section className={className} data-testid={testId}>
			<div className="mb-4 flex items-center justify-between gap-4">
				<h2 className="text-xl md:text-2xl font-bold text-foreground">{title}</h2>
				{action}
			</div>
			{bordered ? (
				<div className={cn('rounded-lg border border-gray-200 bg-white p-5 sm:p-6', bodyClassName)}>
					{children}
				</div>
			) : (
				<div className={bodyClassName}>{children}</div>
			)}
		</section>
	);
};

export default SectionCard;
